<script setup lang="ts">
import { computed } from 'vue'

import type { BetaFile } from '@/types/beta-dashboard'

const props = defineProps<{
    file: BetaFile
    deleting?: boolean
}>()

const emit = defineEmits<{
    copy: [url: string]
    remove: [file: BetaFile]
}>()

const fileUrl = computed(() => `${props.file.domain || ''}${props.file.route_path || ''}`)
const href = computed(() => (
    /^https?:\/\//i.test(fileUrl.value) ? fileUrl.value : `//${fileUrl.value}`
))

const extension = computed(() => {
    const value = String(props.file.name || props.file.route_path || '')
    const part = value.split('.').pop()
    return part && part !== value ? part.slice(0, 4).toUpperCase() : 'FILE'
})
</script>

<template>
    <article class="beta-file-card">
        <span class="beta-file-card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M7 3.5h6.2L18 8.3v12.2H7V3.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                <path d="M13 3.5v5h5" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
            </svg>
            <small>{{ extension }}</small>
        </span>

        <div class="beta-file-card__content">
            <strong :title="file.name">{{ file.name }}</strong>
            <a :href="href" target="_blank" rel="noopener noreferrer">{{ fileUrl }}</a>
        </div>

        <div class="beta-file-card__actions">
            <button type="button" class="is-copy" @click="emit('copy', fileUrl)">Copiar URL</button>
            <button type="button" class="is-remove" :disabled="deleting" @click="emit('remove', file)">
                {{ deleting ? 'Excluindo…' : 'Excluir' }}
            </button>
        </div>
    </article>
</template>

<style scoped>
.beta-file-card {
    display: grid;
    min-width: 0;
    grid-template-columns: 56px minmax(0, 1fr);
    gap: 11px;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    background: #fff;
    padding: 12px;
}

.beta-file-card__icon {
    position: relative;
    display: grid;
    width: 56px;
    height: 62px;
    place-items: center;
    border-radius: 13px;
    background: color-mix(in srgb, var(--main-color) 7%, #f3f6fa);
    color: var(--main-color);
}

.beta-file-card__icon svg {
    width: 30px;
    height: 30px;
}

.beta-file-card__icon small {
    position: absolute;
    right: 6px;
    bottom: 5px;
    max-width: 44px;
    overflow: hidden;
    color: #6f8198;
    font-size: 10px;
    font-weight: 900;
    text-overflow: ellipsis;
}

.beta-file-card__content {
    min-width: 0;
    padding-top: 4px;
}

.beta-file-card__content strong,
.beta-file-card__content a {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-file-card__content strong {
    color: #263d5a;
    font-size: 12px;
    font-weight: 850;
}

.beta-file-card__content a {
    margin-top: 5px;
    color: #8190a4;
    font-size: 10px;
}

.beta-file-card__actions {
    display: flex;
    grid-column: 2;
    gap: 10px;
    margin-top: -24px;
    align-self: end;
}

.beta-file-card__actions button {
    font-size: 9px;
    font-weight: 850;
}

.beta-file-card__actions .is-copy {
    color: var(--main-color);
}

.beta-file-card__actions .is-remove {
    color: #b65353;
}

.beta-file-card__actions button:disabled {
    cursor: wait;
    opacity: 0.55;
}
</style>
